/**
 * SyPI config + schema fetching, schema-default updates, and the "update
 * live config" mutation. All the "current state" reads and writes live here;
 * scheduling / drafting lives in sypi-schedule.
 */

import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import isEqual from "lodash/isEqual";
import { diff } from "deep-object-diff";
import { mergeAdvanced } from "object-merge-advanced";
import { getStore } from "@store";
import sypiAdapter from "@features/sypi/adapters";
import { googleAnalyticsEventSelection } from "@components/shared/google-analytics-events";
import {
  isEmptyObject,
  stringifyNestedKeyPaths,
} from "@utils/object-helpers";
import {
  camelToTitle,
  camelize,
  toSentenceCase,
} from "@utils/string-helpers";
import { gaTag } from "@utils/browser-helpers";
import {
  generateBlankConfig,
  compareConfigs,
  setDisplayCondition,
} from "@features/sypi/_utils/sypi-interface-helpers";
import { CONFIG_SCHEDULER } from "@actions/types";
import { STATE_CONTANTS } from "@constants";
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  reportError,
  clearChanges,
  closeDock,
  clearLog,
  clearErrors,
  clickTab,
  logChangedKey,
  recordChangeObject,
} from "@actions/global";
import {
  loadSchema,
  setSypiDetails,
  updateSypiDetails,
  loadDefaultConfig,
  setFieldVisibility,
  loadMasterSchema,
  loadInitialSchema,
  loadImportedInputs,
  setSypiConfigSaveState,
} from "@features/sypi/actions";
import { schemaFormatter } from "./sypi-shared";
import {
  dispatchErrors,
  attemptRevolvingSchemaErrors,
} from "./sypi-errors";

export const fetchAndLoadSchemaTemplate = async (
  release_version = "",
  store = getStore()
) => {
  const activeReleaseVersion = release_version
    ? release_version
    : store.getState().sypiSchema.activeReleaseVersion;
  const { formattedSchema, formattedMasterSchema } = await fetchSypiSchema(
    activeReleaseVersion
  );
  store.dispatch(loadSchema(formattedSchema));
  store.dispatch(loadInitialSchema(formattedSchema));
  store.dispatch(loadMasterSchema(formattedMasterSchema));
  const configTemplate = generateBlankConfig(formattedSchema);
  store.dispatch(loadDefaultConfig(configTemplate));
  return { configTemplate, formattedSchema };
};

export const updateSchemaConfigDefaults = async (
  actionDetails = {},
  store = getStore()
) => {
  store.dispatch(showSpinner());
  let finalReport = null;
  const { sypiConfigScheduler } = store.getState();
  const { summaryView } = sypiConfigScheduler.generalPreview;
  const { activeReleaseVersion } = store.getState().sypiSchema;
  const { callback } = actionDetails;
  const sypiSchema = await fetchSypiSchema(activeReleaseVersion);
  const { formattedSchema, formattedMasterSchema, rawMasterSchema } =
    sypiSchema;
  const schemasDifference = diff(formattedSchema, formattedMasterSchema);

  store.dispatch(clearChanges());
  store.dispatch(clearErrors());
  store.dispatch(clearLog());

  if (summaryView.length > 0) {
    let updatedImports = {};
    summaryView.forEach((key) => {
      updatedImports = { ...updatedImports, [key.keyName]: key.newKeyValue };
    });
    store.dispatch(loadImportedInputs(updatedImports));
  }
  store.dispatch(setSypiConfigSaveState("updateSchema"));
  const clientConfig = await fetchSypiConfig(activeReleaseVersion);
  const clientConfigTemplate = generateBlankConfig(formattedSchema);
  const sypiClientConfig =
    typeof clientConfig === "string"
      ? clientConfigTemplate
      : clientConfig.sypi_config;
  const shouldInitialSypiLoad = typeof clientConfig === "string";

  const {
    fullErrorReport: { schemaErrors },
    configSchemaError,
  } = dispatchErrors(sypiClientConfig, formattedMasterSchema, "", false);

  if (
    (schemasDifference && !isEmptyObject(schemasDifference)) ||
    (schemaErrors && schemaErrors?.length)
  ) {
    let masterConfigTemplate = generateBlankConfig(formattedMasterSchema);

    const savedConfigChanges = diff(clientConfigTemplate, sypiClientConfig);

    const pathConfigChanges =
      savedConfigChanges && !isEmptyObject(savedConfigChanges)
        ? stringifyNestedKeyPaths(savedConfigChanges)
        : [];

    let updatedSypiConfig = attemptRevolvingSchemaErrors(
      pathConfigChanges,
      sypiClientConfig,
      masterConfigTemplate,
      configSchemaError
    );

    const mergeUpdatedConfig = mergeAndHandleVisibilitySettings(
      masterConfigTemplate,
      updatedSypiConfig,
      false
    );
    store.dispatch(setSypiDetails(mergeUpdatedConfig, shouldInitialSypiLoad));
    store.dispatch(loadSchema(formattedMasterSchema));
    store.dispatch(loadMasterSchema(formattedMasterSchema, rawMasterSchema));
    dispatchErrors(mergeUpdatedConfig, formattedMasterSchema);

    const sypiConfigChangesWithNewDefaults = diff(
      sypiClientConfig,
      updatedSypiConfig
    );

    if (
      (sypiConfigChangesWithNewDefaults &&
        !isEmptyObject(sypiConfigChangesWithNewDefaults)) ||
      (schemaErrors && schemaErrors?.length)
    ) {
      if (
        sypiConfigChangesWithNewDefaults &&
        !isEmptyObject(sypiConfigChangesWithNewDefaults)
      ) {
        const updatedStringPaths = stringifyNestedKeyPaths(
          sypiConfigChangesWithNewDefaults
        );
        const updatedChangeDict = updatedStringPaths.reduce(
          (configAcc, path) => {
            const oldValue = get(sypiClientConfig, path, "");
            const newValue = get(updatedSypiConfig, path, "");
            if (!isEqual(newValue, oldValue)) {
              configAcc[path] = newValue;
            }
            return configAcc;
          },
          {}
        );
        store.dispatch(logChangedKey(Object.keys(updatedChangeDict)));
        store.dispatch(recordChangeObject(updatedChangeDict));
      } else if (schemaErrors && schemaErrors.length) {
        // Schema structure errors exist but no config value changes —
        // log a synthetic entry so hasChanged becomes truthy
        store.dispatch(logChangedKey(["_schemaDefaults"]));
        store.dispatch(recordChangeObject({ _schemaDefaults: true }));
      }

      finalReport = {
        type: "ALERT_DOCK",
        title: `New Schema Configuration Defaults`,
        message: `Schema Configuration Defaults Loaded. Please schedule changes to update the config.`,
      };
    } else {
      finalReport = {
        type: "ALERT_DOCK",
        title: `New Schema Configuration Defaults`,
        message: `No config changes detected. Please schedule schema default changes to update the schema.`,
      };
    }
  } else {
    finalReport = {
      type: "ALERT_DOCK",
      title: `New Schema Configuration Defaults`,
      message: "No changes were found in the Schema Configuration",
    };
  }
  if (callback) {
    callback();
  }
  store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  store.dispatch(stopSpinner());
  return finalReport;
};

export const mergeAndHandleVisibilitySettings = (
  configSchemaTemplate,
  configParam,
  setSypiInitial = true,
  store = getStore()
) => {
  const mergedConfig = mergeAdvanced(configSchemaTemplate, configParam, {
    cb: (inputArg1, inputArg2, resultAboutToBeReturned) =>
      (Array.isArray(inputArg1) &&
        Array.isArray(inputArg2) &&
        inputArg2.length) ||
      typeof inputArg2 === "boolean" ||
      (typeof inputArg2 === "string" && !inputArg2)
        ? inputArg2
        : resultAboutToBeReturned,
  });
  const viewDiff = compareConfigs(configSchemaTemplate, configParam);
  if (store) {
    store.dispatch(setSypiDetails(mergedConfig, setSypiInitial));
    store.dispatch(setFieldVisibility(setDisplayCondition(viewDiff)));
  }
  return mergedConfig;
};

export const fetchConfigByCTID = async (activeReleaseVersion, ctid) => {
  const fetchedCTIDConfig = await sypiAdapter.fetchSypiConfig(
    { release: activeReleaseVersion },
    ctid
  );
  return fetchedCTIDConfig;
};

export const fetchSypiSchema = async (
  activeReleaseVersion = "",
  ctid = ""
) => {
  let fetchedSchemas = {
    formattedSchema: {},
    formattedMasterSchema: {},
    rawMasterSchema: {},
    schemaChanged: false,
  };
  const sypiSchema = await sypiAdapter.fetchSypiSchema(
    { release: activeReleaseVersion },
    ctid
  );
  fetchedSchemas.formattedSchema = schemaFormatter(sypiSchema.config_schema);
  if (sypiSchema.schema_has_changed) {
    fetchedSchemas.schemaChanged = sypiSchema.schema_has_changed;
    fetchedSchemas.rawMasterSchema = cloneDeep(sypiSchema.master_schema);
    fetchedSchemas.formattedMasterSchema = schemaFormatter(
      sypiSchema.master_schema
    );
  }
  return fetchedSchemas;
};

export const fetchSypiConfig = async (release_version) => {
  const config = await sypiAdapter.fetchSypiConfig({
    release: release_version,
  });
  return config;
};

/**
 * Push the current in-memory config to live. Note the original preserved
 * the reference to a bare `activeReleaseVersion` and `status` — those were
 * hoisted globals in the source file (implicit strict-mode issues); we
 * preserve behavior verbatim to avoid changing runtime semantics.
 */
export const updateLiveSypiConfig = async (
  keyTarget,
  sypiNewView,
  updateAction,
  store = getStore()
) => {
  let result = await sypiAdapter.updateSypiConfig({
    // eslint-disable-next-line no-undef
    release: activeReleaseVersion,
    sypi_config: sypiNewView.sypiConfig,
  });
  let { success, errors, type, error_elements } = result;
  // eslint-disable-next-line no-undef
  if ((errors && !errors.length) || success || status === 200) {
    updateAction = "updated";
    store.dispatch(
      setToastMessage(
        `${camelToTitle(camelize(keyTarget))} changes have been ${updateAction}`
      )
    );
    store.dispatch(updateSypiDetails());
    let gaPayloadReady = diff(
      sypiNewView.initialConfig,
      sypiNewView.sypiConfig
    );
    googleAnalyticsEventSelection("Update", {
      id: gaTag(),
      payload: gaPayloadReady,
      category: "SYPI V2",
    });
    store.dispatch(closeDock());
  } else {
    store.dispatch(closeDock());
    store.dispatch(
      reportError(
        `Updating the file was unsuccessful. ${toSentenceCase(result)}`
      )
    );
  }
};
